import { prisma } from "@/lib/db";
import {
  COLD_START_EASY_COUNT,
  COLD_START_HARD_COUNT,
  COLD_START_SESSION_SIZE,
  EASY_THRESHOLD,
  HARD_THRESHOLD_1,
  HARD_THRESHOLD_2,
  NORMAL_EASY_COUNT,
  NORMAL_HARD_COUNT,
  NORMAL_RANDOM_COUNT,
  NORMAL_SESSION_SIZE,
  RETRY_DELAY_PROBLEMS,
} from "./constants";
import { calculatePCorrectFromLogs, initializeFocusSet } from "./predictor";
import type { PresentedType, SessionProblem } from "./types";

interface ProblemWithPCorrect {
  problemId: string;
  problemText: string;
  groupId: string;
  set: string;
  pCorrect: number;
  lastResult: string | null;
}

/**
 * Shuffle array in place (Fisher-Yates)
 */
function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Load problems with their p_correct values
 */
async function loadProblemsWithPCorrect(
  userId: string,
  sets: string[]
): Promise<ProblemWithPCorrect[]> {
  const now = new Date();

  // Get all problems in the specified sets
  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      set: { in: sets },
    },
    include: {
      problem: true,
    },
  });

  // Get recent review logs for all these problems (for p_correct calculation)
  const problemIds = states.map(s => s.problemId);
  const logs = await prisma.reviewLog.findMany({
    where: {
      userId,
      problemId: { in: problemIds },
    },
    orderBy: { timestamp: "desc" },
  });

  // Group logs by problemId
  const logsByProblem = new Map<string, Array<{ timestamp: Date; wasCorrect: boolean }>>();
  for (const log of logs) {
    const existing = logsByProblem.get(log.problemId) || [];
    if (existing.length < 10) {
      existing.push({ timestamp: log.timestamp, wasCorrect: log.wasCorrect });
    }
    logsByProblem.set(log.problemId, existing);
  }

  // Calculate p_correct for each problem
  return states.map(s => ({
    problemId: s.problemId,
    problemText: s.problem.problemText,
    groupId: s.problem.groupId,
    set: s.set,
    pCorrect: calculatePCorrectFromLogs(logsByProblem.get(s.problemId) || [], now),
    lastResult: s.lastResult,
  }));
}

/**
 * Build a session queue according to Focus Set rules
 */
export async function buildSessionQueue(userId: string): Promise<SessionProblem[]> {
  // Ensure user has a Focus set initialized
  await initializeFocusSet(userId);

  // Check if Old set exists
  const oldCount = await prisma.userProblemState.count({
    where: { userId, set: "old" },
  });

  const isColdStart = oldCount === 0;

  if (isColdStart) {
    return buildColdStartSession(userId);
  } else {
    return buildNormalSession(userId);
  }
}

/**
 * Cold start session: 10 problems from Focus only
 * - 3 Easy (p > 80%)
 * - 7 Hard (p < 50%, fallback to p < 70%)
 */
async function buildColdStartSession(userId: string): Promise<SessionProblem[]> {
  const allFocusProblems = await loadProblemsWithPCorrect(userId, ["focus"]);

  // Exclude problems already answered correctly - they don't need more practice
  const focusProblems = allFocusProblems.filter(p => p.lastResult !== "correct");

  const queue: SessionProblem[] = [];

  // Split into easy and hard pools (from problems that still need work)
  const easyPool = shuffle(focusProblems.filter(p => p.pCorrect > EASY_THRESHOLD));
  const hardPool1 = shuffle(focusProblems.filter(p => p.pCorrect < HARD_THRESHOLD_1));
  const hardPool2 = shuffle(focusProblems.filter(p => p.pCorrect >= HARD_THRESHOLD_1 && p.pCorrect < HARD_THRESHOLD_2));

  const usedIds = new Set<string>();

  // Pick easy problems
  for (const p of easyPool) {
    if (queue.filter(q => q.type === "easy").length >= COLD_START_EASY_COUNT) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "easy",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
    }
  }

  // Pick hard problems - cascade through pools
  let hardCount = 0;
  const targetHard = COLD_START_HARD_COUNT;

  // First: p < 50%
  for (const p of hardPool1) {
    if (hardCount >= targetHard) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "hard",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
      hardCount++;
    }
  }

  // Fallback: p < 70%
  for (const p of hardPool2) {
    if (hardCount >= targetHard) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "hard",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
      hardCount++;
    }
  }

  // If still not enough hard, fill with any remaining Focus problems
  const remaining = shuffle(focusProblems.filter(p => !usedIds.has(p.problemId)));
  for (const p of remaining) {
    if (queue.length >= COLD_START_SESSION_SIZE) break;
    queue.push({
      problemId: p.problemId,
      problemText: p.problemText,
      groupId: p.groupId,
      type: "hard",
      pCorrect: p.pCorrect,
    });
    usedIds.add(p.problemId);
  }

  return shuffle(queue);
}

/**
 * Normal session: 12 problems
 * - 3 Easy from Focus + Old (p > 80%)
 * - 7 Hard from Focus (with cascade)
 * - 2 Random from Old
 */
async function buildNormalSession(userId: string): Promise<SessionProblem[]> {
  const [allFocusProblems, oldProblems] = await Promise.all([
    loadProblemsWithPCorrect(userId, ["focus"]),
    loadProblemsWithPCorrect(userId, ["old"]),
  ]);

  // Exclude Focus problems already answered correctly - they don't need more practice
  const focusProblems = allFocusProblems.filter(p => p.lastResult !== "correct");

  // For easy pool, we CAN include already-correct problems from Old (they're review)
  // But Focus problems that are correct should not be re-served
  const allProblems = [...focusProblems, ...oldProblems];
  const queue: SessionProblem[] = [];
  const usedIds = new Set<string>();

  // Easy pool: Focus (not yet correct) + Old with p > 80%
  const easyPool = shuffle(allProblems.filter(p => p.pCorrect > EASY_THRESHOLD));

  // Hard pools from Focus only (excluding already correct)
  const hardPool1 = shuffle(focusProblems.filter(p => p.pCorrect < HARD_THRESHOLD_1));
  const hardPool2 = shuffle(focusProblems.filter(p => p.pCorrect >= HARD_THRESHOLD_1 && p.pCorrect < HARD_THRESHOLD_2));

  // Weakest from Old (for hard fallback)
  const weakestOld = [...oldProblems].sort((a, b) => a.pCorrect - b.pCorrect);

  // Random pool from Old
  const randomPool = shuffle(oldProblems);

  // 1. Pick 3 easy problems
  for (const p of easyPool) {
    if (queue.filter(q => q.type === "easy").length >= NORMAL_EASY_COUNT) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "easy",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
    }
  }

  // 2. Pick 7 hard problems with cascade
  let hardCount = 0;
  const targetHard = NORMAL_HARD_COUNT;

  // First: p < 50% from Focus
  for (const p of hardPool1) {
    if (hardCount >= targetHard) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "hard",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
      hardCount++;
    }
  }

  // Fallback: p < 70% from Focus
  for (const p of hardPool2) {
    if (hardCount >= targetHard) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "hard",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
      hardCount++;
    }
  }

  // Final fallback: weakest from Old
  for (const p of weakestOld) {
    if (hardCount >= targetHard) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "hard",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
      hardCount++;
    }
  }

  // 3. Pick 2 random from Old
  let randomCount = 0;
  for (const p of randomPool) {
    if (randomCount >= NORMAL_RANDOM_COUNT) break;
    if (!usedIds.has(p.problemId)) {
      queue.push({
        problemId: p.problemId,
        problemText: p.problemText,
        groupId: p.groupId,
        type: "random",
        pCorrect: p.pCorrect,
      });
      usedIds.add(p.problemId);
      randomCount++;
    }
  }

  return shuffle(queue);
}

export interface SessionState {
  queue: SessionProblem[];
  position: number;
  retryQueue: string[]; // Problem IDs that were wrong and need retry
  recentShown: string[]; // Last few shown problem IDs
  consecutiveWrong: number;
}

/**
 * Get next problem from session, handling retries and consecutive wrong
 */
export function getNextFromSession(state: SessionState): {
  problem: SessionProblem | null;
  newState: SessionState;
} {
  // Check if we need to give an easy problem (2+ consecutive wrong)
  const needsEasyProblem = state.consecutiveWrong >= 2;

  // Check for retries first (if enough problems have passed)
  if (state.retryQueue.length > 0) {
    // Find a retry that wasn't shown in last RETRY_DELAY_PROBLEMS
    const recentSet = new Set(state.recentShown.slice(-RETRY_DELAY_PROBLEMS));
    const retryIndex = state.retryQueue.findIndex(id => !recentSet.has(id));

    if (retryIndex >= 0) {
      const retryId = state.retryQueue[retryIndex];
      const retryProblem = state.queue.find(p => p.problemId === retryId);

      if (retryProblem) {
        // If we need easy but retry isn't easy, skip retry this time
        if (needsEasyProblem && retryProblem.pCorrect < EASY_THRESHOLD) {
          // Look for easy problem in queue instead
          const easyProblem = state.queue.find(
            p => p.pCorrect > EASY_THRESHOLD && !state.recentShown.slice(-3).includes(p.problemId)
          );
          if (easyProblem) {
            return {
              problem: { ...easyProblem, type: "easy" },
              newState: {
                ...state,
                recentShown: [...state.recentShown.slice(-9), easyProblem.problemId],
              },
            };
          }
        }

        // Serve the retry
        const newRetryQueue = [...state.retryQueue];
        newRetryQueue.splice(retryIndex, 1);

        return {
          problem: { ...retryProblem, type: "retry" },
          newState: {
            ...state,
            retryQueue: newRetryQueue,
            recentShown: [...state.recentShown.slice(-9), retryId],
          },
        };
      }
    }
  }

  // If we need easy problem, try to find one
  if (needsEasyProblem) {
    const easyProblem = state.queue.find(
      p => p.pCorrect > EASY_THRESHOLD && !state.recentShown.slice(-3).includes(p.problemId)
    );
    if (easyProblem) {
      return {
        problem: { ...easyProblem, type: "easy" },
        newState: {
          ...state,
          recentShown: [...state.recentShown.slice(-9), easyProblem.problemId],
        },
      };
    }
  }

  // Normal queue progression
  if (state.position >= state.queue.length) {
    return { problem: null, newState: state };
  }

  // Find next problem not recently shown
  for (let i = state.position; i < state.queue.length; i++) {
    const problem = state.queue[i];
    if (!state.recentShown.slice(-3).includes(problem.problemId)) {
      return {
        problem,
        newState: {
          ...state,
          position: i + 1,
          recentShown: [...state.recentShown.slice(-9), problem.problemId],
        },
      };
    }
  }

  // If all remaining were recently shown, just take next
  const problem = state.queue[state.position];
  return {
    problem: problem || null,
    newState: {
      ...state,
      position: state.position + 1,
      recentShown: problem
        ? [...state.recentShown.slice(-9), problem.problemId]
        : state.recentShown,
    },
  };
}

/**
 * Update session state after answer
 */
export function updateSessionAfterAnswer(
  state: SessionState,
  problemId: string,
  wasCorrect: boolean
): SessionState {
  if (wasCorrect) {
    // Remove from retry queue if present
    return {
      ...state,
      retryQueue: state.retryQueue.filter(id => id !== problemId),
      consecutiveWrong: 0,
    };
  } else {
    // Add to retry queue if not already there
    const retryQueue = state.retryQueue.includes(problemId)
      ? state.retryQueue
      : [...state.retryQueue, problemId];

    return {
      ...state,
      retryQueue,
      consecutiveWrong: state.consecutiveWrong + 1,
    };
  }
}
